import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Activity, CheckCircle2, Loader2, Filter, Mic } from "lucide-react";
import AdminVoiceFillModal from "@/components/AdminVoiceFillModal";

const KIND_LABEL = {
  fall: "Fall",
  behaviour: "Behaviour",
  injury: "Injury",
  medication_incident: "Medication",
};
const SEVERITY_COLOR = {
  none: "#64748B",
  minor: "#F59E0B",
  moderate: "#EF4444",
  severe: "#B91C1C",
};

function formatWhen(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

function IncidentRow({ inc, onReview }) {
  const [open, setOpen] = useState(false);
  const [savingReview, setSavingReview] = useState(false);
  const [notes, setNotes] = useState(inc.manager_notes || "");
  const [plan, setPlan] = useState(inc.prevention_plan || "");
  const [voiceOpen, setVoiceOpen] = useState(false);

  const submitReview = async () => {
    setSavingReview(true);
    try {
      const { data } = await api.post(`/admin/incidents/${inc.id}/review`, {
        manager_notes: notes, prevention_plan: plan,
      });
      toast.success("Incident reviewed");
      onReview?.(data);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save review");
    } finally {
      setSavingReview(false);
    }
  };

  const reviewed = !!inc.reviewed_at;
  return (
    <div
      className={`card-surface p-4 ${reviewed ? "opacity-75" : ""}`}
      data-testid={`incident-row-${inc.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span
              className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg"
              style={{
                background: (SEVERITY_COLOR[inc.injury_severity] || "#EF4444") + "1a",
                color: SEVERITY_COLOR[inc.injury_severity] || "#EF4444",
              }}
            >
              {inc.kind === "fall" || inc.kind === "injury" ? <Activity size={16} /> : <AlertTriangle size={16} />}
            </span>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-clinical-text">
                  {KIND_LABEL[inc.kind]} — {inc.client_name}
                </span>
                {reviewed && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700">
                    <CheckCircle2 size={10} /> Reviewed
                  </span>
                )}
              </div>
              <div className="text-xs text-clinical-subtext">
                {formatWhen(inc.occurred_at)} · {inc.caregiver_name}
                {inc.location && ` · ${inc.location}`}
              </div>
            </div>
          </div>
          {(inc.kind === "fall" || inc.kind === "injury") && inc.injury_severity && inc.injury_severity !== "none" && (
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide"
              style={{
                background: SEVERITY_COLOR[inc.injury_severity] + "1a",
                color: SEVERITY_COLOR[inc.injury_severity],
              }}
            >
              {inc.injury_severity}
            </span>
          )}
          {inc.critical && (
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase text-red-700">
              Critical
            </span>
          )}
        </div>
      </button>

      {open && (
        <div className="mt-3 space-y-3 border-t border-clinical-border/40 pt-3 text-sm">
          <div>
            <div className="label-eyebrow">Description</div>
            <p className="mt-1 text-clinical-text">{inc.description}</p>
          </div>
          {(inc.kind === "fall" || inc.kind === "injury") && inc.body_part && (
            <div>
              <span className="text-clinical-subtext">Body part: </span>
              <span className="font-medium">{inc.body_part}</span>
              {inc.injury_mechanism && (
                <span className="ml-2 text-clinical-subtext">· {inc.injury_mechanism}</span>
              )}
              {inc.er_transferred && <span className="ml-2 text-red-700">· ER transferred</span>}
              {inc.first_aid_given && <span className="ml-2 text-green-700">· First aid given</span>}
            </div>
          )}
          {inc.kind === "medication_incident" && (
            <div className="text-sm">
              <span className="text-clinical-subtext">Medication: </span>
              <span className="font-medium">{inc.med_name}</span>
              {inc.med_dose && <span className="ml-1 text-clinical-subtext">({inc.med_dose})</span>}
              {inc.med_error_type && (
                <span className="ml-2 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                  {inc.med_error_type.replace(/_/g, " ")}
                </span>
              )}
              <div className="mt-1 text-xs text-clinical-subtext">
                {inc.pharmacy_contacted && "✓ Pharmacy notified · "}
                {inc.doctor_contacted && "✓ Doctor notified"}
              </div>
            </div>
          )}
          {inc.kind === "behaviour" && (
            <>
              {inc.antecedent && (
                <div>
                  <div className="label-eyebrow">Antecedent</div>
                  <p className="mt-1">{inc.antecedent}</p>
                </div>
              )}
              <div className="text-xs text-clinical-subtext">
                {inc.behaviour_duration_minutes}m · function: {inc.function}
                {inc.prn_administered && " · PRN given"}
              </div>
            </>
          )}
          <div className="flex flex-wrap gap-2 text-xs">
            {inc.family_notified && (
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-700">Family notified</span>
            )}
            {inc.manager_notified && (
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-700">Manager notified</span>
            )}
          </div>
          {!reviewed && (
            <div className="rounded-lg bg-clinical-bg/40 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="label-eyebrow">Manager review</div>
                <button
                  type="button"
                  onClick={() => setVoiceOpen(true)}
                  className="inline-flex items-center gap-1 rounded-lg bg-purple-100 px-2 py-1 text-[11px] font-semibold text-purple-700 hover:bg-purple-200"
                  data-testid={`incident-voice-review-${inc.id}`}
                >
                  <Mic size={11} /> Voice
                </button>
              </div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Manager notes…"
                className="w-full rounded-lg border border-clinical-border bg-white px-3 py-2 text-sm"
                data-testid={`incident-review-notes-${inc.id}`}
              />
              <textarea
                value={plan}
                onChange={(e) => setPlan(e.target.value)}
                rows={2}
                placeholder="Prevention plan…"
                className="w-full rounded-lg border border-clinical-border bg-white px-3 py-2 text-sm"
                data-testid={`incident-review-plan-${inc.id}`}
              />
              <button
                type="button"
                onClick={submitReview}
                disabled={savingReview}
                className="rounded-lg bg-clinical-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                data-testid={`incident-review-submit-${inc.id}`}
              >
                {savingReview ? <Loader2 size={12} className="inline animate-spin" /> : "Mark reviewed"}
              </button>
              <AdminVoiceFillModal
                open={voiceOpen}
                onClose={() => setVoiceOpen(false)}
                endpoint="/ai/voice-fill/incident-review"
                title="Voice incident review"
                hint="Dictate your review, the action plan, and any follow-up needed."
                examples={[
                  "After reviewing this fall, we will retrain Sarah on hoyer lifts and call the family today. Severity moderate. Follow up in 7 days.",
                  "Incident is resolved. The family understands. Closing this out.",
                  "This needs PIP — Sarah missed the medication twice this month. Schedule a 1 on 1 tomorrow.",
                ]}
                limitMs={120 * 1000}
                filenamePrefix="incident-review"
                testIdPrefix={`incident-voice-${inc.id}`}
                renderPreview={(r) => (
                  <div className="space-y-2 text-sm">
                    {r.manager_notes && (
                      <div>
                        <div className="label-eyebrow">Notes</div>
                        <p className="text-clinical-text">{r.manager_notes}</p>
                      </div>
                    )}
                    {r.prevention_plan && (
                      <div>
                        <div className="label-eyebrow">Prevention plan</div>
                        <p className="text-clinical-text">{r.prevention_plan}</p>
                      </div>
                    )}
                    {(r.action_items || []).length > 0 && (
                      <div>
                        <div className="label-eyebrow">Action items</div>
                        <ul className="list-disc pl-5 text-clinical-text">
                          {r.action_items.map((a, i) => (<li key={i}>{a}</li>))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                onConfirm={(r) => {
                  if (r.manager_notes) {
                    setNotes((curr) => (curr ? `${curr}\n${r.manager_notes}` : r.manager_notes));
                  }
                  const planText = [
                    r.prevention_plan || "",
                    (r.action_items || []).map((a) => `• ${a}`).join("\n"),
                  ].filter(Boolean).join("\n");
                  if (planText) {
                    setPlan((curr) => (curr ? `${curr}\n${planText}` : planText));
                  }
                  toast.success("Review filled from voice");
                }}
                confirmLabel="Use this"
              />
            </div>
          )}
          {reviewed && (
            <div className="rounded-lg bg-green-50/50 p-3 text-xs text-green-900">
              <div className="font-medium">Reviewed by {inc.reviewed_by} on {formatWhen(inc.reviewed_at)}</div>
              {inc.manager_notes && <div className="mt-1">{inc.manager_notes}</div>}
              {inc.prevention_plan && (
                <div className="mt-1"><span className="font-medium">Prevention: </span>{inc.prevention_plan}</div>
              )}
            </div>
          )}
          <div className="text-[10px] text-clinical-subtext">
            <Link to={`/admin/clients?client=${inc.client_id}`} className="underline">
              Open client record →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminIncidents() {
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState(searchParams.get("kind") || "");
  const [unreviewedOnly, setUnreviewedOnly] = useState(false);
  const [days, setDays] = useState(parseInt(searchParams.get("days") || "30", 10));

  // Honour query-param updates (admin voice command navigation)
  useEffect(() => {
    const k = searchParams.get("kind") || "";
    if (k !== kind) setKind(k);
    const d = parseInt(searchParams.get("days") || "30", 10);
    if (d !== days) setDays(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days };
      if (kind) params.kind = kind;
      if (unreviewedOnly) params.unreviewed_only = true;
      const { data } = await api.get("/admin/incidents", { params });
      setItems(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load incidents");
    } finally {
      setLoading(false);
    }
  }, [days, kind, unreviewedOnly]);

  useEffect(() => { load(); }, [load]);

  const updateOne = (updated) =>
    setItems((arr) => arr.map((i) => (i.id === updated.id ? updated : i)));

  const unreviewedCount = items.filter((i) => !i.reviewed_at).length;

  return (
    <div className="space-y-4" data-testid="admin-incidents-page">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-clinical-text">Incidents</h1>
          <p className="text-sm text-clinical-subtext">
            Caregiver-reported incidents (falls, behaviour, injuries, medication errors) · {unreviewedCount} unreviewed
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Filter size={14} className="text-clinical-subtext" />
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="rounded-lg border border-clinical-border bg-white px-2 py-1 text-sm"
            data-testid="incidents-filter-kind"
          >
            <option value="">All kinds</option>
            <option value="fall">Falls</option>
            <option value="behaviour">Behaviour</option>
            <option value="injury">Injury (CI)</option>
            <option value="medication_incident">Medication</option>
          </select>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-lg border border-clinical-border bg-white px-2 py-1 text-sm"
            data-testid="incidents-filter-days"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
          <label className="flex items-center gap-1.5 text-xs">
            <input
              type="checkbox"
              checked={unreviewedOnly}
              onChange={(e) => setUnreviewedOnly(e.target.checked)}
              data-testid="incidents-filter-unreviewed"
            />
            Unreviewed only
          </label>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 size={20} className="animate-spin text-clinical-subtext" />
        </div>
      ) : items.length === 0 ? (
        <div className="card-surface py-12 text-center">
          <CheckCircle2 size={28} className="mx-auto text-green-500 mb-2" />
          <p className="text-clinical-subtext">No incidents in the selected window. Nice work.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((inc) => (
            <IncidentRow key={inc.id} inc={inc} onReview={updateOne} />
          ))}
        </div>
      )}
    </div>
  );
}
